/**
 * Sorts location list to show metadata locations first, followed by the rest.
 *
 * @param {Array} locationList - Array of location objects.
 * @param {Array} metadataLocationIds - Array of location IDs from metadata.
 * @returns {Array} - Sorted array with metadata locations first.
 */
export function sortedLocationList(locationList, metadataLocationIds) {
  const locations = [...locationList];
  if (!metadataLocationIds || !Array.isArray(metadataLocationIds) || metadataLocationIds.length === 0) {
    return locations;
  }
  const metadataLocations = [];
  metadataLocationIds.forEach(id => {
    const location = locations.find(loc => loc._id === id);
    if (location) {
      metadataLocations.push(location);
    }
  });
  return [...metadataLocations];
}

/**
 * Loads transaction matching analysis from the API.
 *
 * @param {Object} param - The parameters for the API request.
 * @param {string} param.id - Analysis ID.
 * @param {string} param.pid - Project ID.
 * @param {string} param.bid - Business unit ID.
 * @param {Object} axiosInstance - Axios instance to be used for the request.
 * @returns {Promise<Object>} - The API response data.
 * @throws Will throw an error if parameters are missing or the API call fails.
 */
export async function loadTransactionMatchingAnalysis(param, axiosInstance) {
  const { id, pid, bid } = param || {};

  if (!id || !pid || !bid) {
    throw new Error("Missing required parameters: id, pid, and bid are all required.");
  }

  try {
    const url = `/v2/bot/analysis/loadTransactionMatchingAnalysis/${id}`;
    const payload = { data: { pid, bid } };

    const response = await axiosInstance.post(url, payload);

    if (response.status !== 200) {
      throw new Error(`API responded with status code ${response.status}`);
    }

    return response.data;
  } catch (error) {
    console.error("Failed to load transaction matching analysis:", error);
    throw new Error("Unable to load transaction matching analysis. Please try again.");
  }
}

/**
 * Loads overall reconciliation summary from the API.
 *
 * @param {Object} param - The parameters for the API request..
 * @param {Object} axiosInstance - Axios instance to be used for the request.
 * @returns {Promise<Object>} - The API response data.
 * @throws Will throw an error if parameters are missing or the API call fails.
 */
export async function loadOverallReconciliationSummary(param, axiosInstance) {
  if (!param) {
    throw new Error("Missing required parameters: collection required.");
  }

  try {
    const url = `/v2/bot/analysis/readcombocodewisereconciliation/overallreconsummary`;
    const payload = { data: { collection: param } };

    const response = await axiosInstance.post(url, payload);

    if (response.status !== 200) {
      throw new Error(`API responded with status code ${response.status}`);
    }

    return response.data;
  } catch (error) {
    console.error("Failed to load overall reconciliation summary:", error);
    throw new Error("Unable to load reconciliation summary. Please try again.");
  }
}
